
import {
  all, call, put, takeLatest, select
} from 'redux-saga/effects';
import { push } from 'connected-react-router';

import {
  HANDLE_AUTHENTICATION_CALLBACK,
  SILENT_AUTH_CHECK,
  showNotification,
  SIGN_IN,
  SIGN_IN_SUCCESS,
  SIGN_OUT_SUCCESS,
  SIGN_IN_FAILURE,
  SIGN_OUT,
  crudGetOne
} from '../actions';
import log from '../../log';

import { handleFetch } from './fetch';

function* handleUserRoles(authProvider, dataProvider, config, user) {
  if (!user.roles) {
    if (config.roleSetup) {
      const fetchAction = crudGetOne(config.roleSetup.endpoint, null, config.roleSetup.query(user));
      const roleResponse = yield call(handleFetch, dataProvider, authProvider, fetchAction);
      log.info('Role Response: ', roleResponse);
      user = roleResponse.data;
    } else if (config.roleFunction) {
      user.roles = yield call(config.roleFunction, user, dataProvider, authProvider);
    } else {
      yield put({ type: SIGN_IN_FAILURE });
      return false;
    }
  }
}

export function* authCallback(authProvider, dataProvider, config, action) {
  console.log('AuthCallback Called - yielding HANDLE_AUTHENTICATION_CALLBACK');
  try {
    const authResponse = yield call(authProvider, 'authCallback', action.payload);
    if (authResponse.authenticated === false) {
      yield put({ type: SIGN_IN_FAILURE, silent: false });
      throw new Error('Error in auth callback');
    } else {
      let user = authResponse.user;
      yield handleUserRoles(authProvider, dataProvider, config, user);
      yield put({ type: SIGN_IN_SUCCESS, user: user });
      if (authResponse.redirectTo) yield put(push(authResponse.redirectTo));
    }
  } catch (error) {
    log.error(error);
    yield put(showNotification('auth.sign_in_error', 'error'));
    yield put(push(config.auth_error_page || '/login_error'));
  }
}

export function* silentAuth(authProvider, dataProvider, config, action) {
  console.log('silentAuth Called - yielding SILENT_AUTH_CHECK');
  const auth = yield select(s => s.auth);
  if (auth.isLoggingIn) {
    // It's already logging in, ignore and wait
    console.log('already logging in')
  } else {
    try {
      const authResponse = yield call(authProvider, 'silentAuthCheck', {
        email: action.email || null
      });
      if (authResponse.authenticated === false) {
        sessionStorage.setItem('imminently_last_page', window.location.href);
        console.log('SILENT FAILURE', action.forceLogin);
        // They need to login
        if (action.forceLogin) {
          yield put({ type: SIGN_IN });
        }
      } else if (authResponse.authenticated === true) {
        // They are authenticated
        let user = authResponse.user;
        yield handleUserRoles(authProvider, dataProvider, config, user);
        yield put({ type: SIGN_IN_SUCCESS, user: user });
        if (authResponse.redirectTo) yield put(push(authResponse.redirectTo));
        // timeout test
        // setTimeout(() => {
        //   console.log('timeout test')
        //   yield put({ type: SILENT_AUTH_CHECK, forceLogin: action.forceLogin });
        // }, 1000 * 10);
      }
    } catch (error) {
      console.log('error silent auth check', error);
      log.error(error);
      yield put(showNotification('auth.sign_in_error', 'error'));
      yield put(push(config.auth_error_page || '/login_error'));
    }
  }
}

export function* signIn(authProvider, dataProvider, config, action) {
  console.log('SignIn Called - yielding SIGN_IN_ATTEMPT');
  try {
    const authResponse = yield call(authProvider, 'signIn', {
      email: action.email || null
    });

    if (authResponse.authenticating) {
      // The provider is redirect or doing something else async to allow log in.
      // Note anything using oidc will use this path
      return false;
    }

    if (authResponse.authenticated === false) {
      sessionStorage.setItem('imminently_last_page', window.location.href);
      yield put({ type: SIGN_IN_FAILURE, silent: false });
    } else if (authResponse.authenticated === true) {
      // They are authenticated
      let user = authResponse.user;
      yield handleUserRoles(authProvider, dataProvider, config, user);
      yield put({ type: SIGN_IN_SUCCESS, user: user });
      if (authResponse.redirectTo) yield put(push(authResponse.redirectTo));
    }
  } catch (error) {
    console.log('error signing in', error);
    log.error(error);
    yield put(showNotification('auth.sign_in_error', 'error'));
    yield put(push(config.auth_error_page || '/login_error'));
  }
}

export function* signOut(authProvider, action) {
  try {
    yield call(authProvider, 'signOut');
    yield put({ type: SIGN_OUT_SUCCESS });
  } catch (error) {
    log.error(error);
    yield put(showNotification('auth.sign_out_error', 'error'));
  }
}

const delay = time => new Promise(resolve => setTimeout(resolve, time));

export function* onSignInSuccess(config, action) {
  const refreshInterval = config.refreshInterval || 1000 * 60 * 10; // defaults to 10 minutes
  yield delay(refreshInterval);
  yield put({ type: SILENT_AUTH_CHECK, forceLogin: false });
}

export default (authProvider, dataProvider, config = {}) => function* watchAuthActions() {
  yield all([
    takeLatest(SIGN_IN, signIn, authProvider, dataProvider, config),
    takeLatest(SIGN_OUT, signOut, authProvider),
    takeLatest(SILENT_AUTH_CHECK, silentAuth, authProvider, dataProvider, config),
    takeLatest(HANDLE_AUTHENTICATION_CALLBACK, authCallback, authProvider, dataProvider, config),
    takeLatest(SIGN_IN_SUCCESS, onSignInSuccess, config)
  ]);
};
